import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Filter state kept in the URL rather than in the component.
 *
 * <p>Held in {@code useState}, a filter lives exactly as long as the screen does. Open an item
 * from a filtered list and come back, and the list is unfiltered again — the state unmounted with
 * the page. That is the whole of the bug: nothing is lost, there was simply never anywhere for it
 * to be kept.
 *
 * <p>The address bar is that place. The browser already remembers URLs, so Back restores the
 * exact list somebody left, and a filtered view becomes something they can send to a colleague.
 *
 * <p>Changing a filter <b>replaces</b> the history entry rather than pushing one. Pushing would
 * make Back undo filters one keystroke at a time instead of leaving the page, which is worse than
 * the problem being fixed. Opening an item is an ordinary push, so Back from the item returns
 * here with everything still set.
 *
 * <p>Values equal to their default are left out of the URL, so an untouched screen has a clean
 * address and the parameters that are there are the ones somebody chose.
 *
 * <p>Two updates in one event compose. React Router resolves a functional update against the
 * params of the render it was called from rather than against anything an earlier call in the
 * same tick wrote, so a pair like "fifty rows a page, and back to page one" would otherwise both
 * start from the old address and the second would navigate over the first — losing the change
 * silently, which reads as a control that does nothing.
 */
export function useUrlFilters<T extends Record<string, string>>(
  defaults: T,
): [T, (patch: Partial<T>) => void] {
  const [params, setParams] = useSearchParams();

  const values = useMemo(() => {
    const current = { ...defaults };
    for (const key of Object.keys(defaults) as (keyof T & string)[]) {
      const fromUrl = params.get(key);
      if (fromUrl !== null) {
        current[key] = fromUrl as T[keyof T & string];
      }
    }
    return current;
  }, [params, defaults]);

  /* What was last written, so a second update in the same tick builds on the first rather than
     on the stale params. Resynced from the committed params on every render, so it never drifts
     from the address bar once React has caught up. */
  const written = useRef(params);
  written.current = params;

  const update = useCallback(
    (patch: Partial<T>) => {
      const next = new URLSearchParams(written.current);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === "" || value === defaults[key]) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      written.current = next;
      setParams(next, { replace: true });
    },
    [setParams, defaults],
  );

  return [values, update];
}
